import * as React from 'react';
import { Clock } from 'lucide-react';

import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

interface TimePickerProps {
  id: string;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  hasError?: boolean;
  className?: string;
}

type TimeInputElement = HTMLInputElement & {
  showPicker?: () => void;
};

const TimePicker = React.forwardRef<HTMLInputElement, TimePickerProps>(
  ({ id, value, onChange, onBlur, name, hasError, className }, ref) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    const setInputRef = (node: HTMLInputElement | null) => {
      inputRef.current = node;

      if (typeof ref === 'function') {
        ref(node);
        return;
      }

      if (ref) {
        ref.current = node;
      }
    };

    const openTimePicker = () => {
      const input = inputRef.current as TimeInputElement | null;
      input?.focus();

      try {
        input?.showPicker?.();
      } catch {
        // Some browsers only allow showPicker during specific user gestures.
      }
    };

    return (
      <div className="relative">
        <Input
          id={id}
          ref={setInputRef}
          name={name}
          type="time"
          step="1"
          value={value || ''}
          onChange={event => onChange(event.target.value)}
          onClick={openTimePicker}
          onBlur={onBlur}
          className={cn(
            'bg-background pr-10 appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none',
            hasError && 'border-red-500',
            className
          )}
        />
        <Clock className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      </div>
    );
  }
);
TimePicker.displayName = 'TimePicker';

export default TimePicker;
